import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { realEstateUsageAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage, StateBlock } from '../Common/admin';

const NS = 'propertyUsage.view';

const RealEstateUsageView = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadRecord = useCallback(async () => {
    try {
      setLoading(true);
      const response = await realEstateUsageAPI.getById(id);
      setRecord(response.data);
    } catch (err) {
      setErrorMessage(t(`${NS}.messages.loadError`));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const headerActions = (
    <div className="ad-toolbar-group">
      <button type="button" className="ad-btn" onClick={() => navigate('/realestateusage')}>
        <i className="fas fa-arrow-right"></i>
        {t(`${NS}.backToList`)}
      </button>
      <button
        type="button"
        className="ad-btn ad-btn-primary"
        onClick={() => navigate(`/realestateusage/edit/${id}`)}
      >
        <i className="fas fa-pen"></i>
        {t(`${NS}.edit`)}
      </button>
    </div>
  );

  if (loading) {
    return (
      <AdminPage title={t(`${NS}.title`)}>
        <StateBlock icon="fa-hourglass-half" title={t('common.loading')} />
      </AdminPage>
    );
  }

  if (!record) {
    return (
      <AdminPage title={t(`${NS}.title`)}>
        <StateBlock icon="fa-circle-exclamation" title={t('propertyUsage.list.messages.noData')} />
        <ErrorModal
          show={showErrorModal}
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />
      </AdminPage>
    );
  }

  const items = [
    ['nameAr', record.nameAr],
    ['nameEng', record.nameEng],
    [
      'createdAt',
      record.createdAt ? new Date(record.createdAt).toLocaleDateString() : null,
    ],
  ];

  return (
    <AdminPage
      title={record.nameAr || t(`${NS}.title`)}
      subtitle={record.nameEng}
      headerActions={headerActions}
    >
      <div className="ad-detail">
        <div className="ad-detail-grid">
          {items.map(([key, value]) => (
            <div key={key} className="ad-detail-item">
              <span className="ad-detail-label">{t(`${NS}.fields.${key}`)}</span>
              <span className="ad-detail-value">
                {value === null || value === undefined || value === '' ? '-' : value}
              </span>
            </div>
          ))}

          <div className="ad-detail-item">
            <span className="ad-detail-label">{t(`${NS}.fields.isActive`)}</span>
            <span>
              <span
                className={`ad-badge ${record.isActive ? 'ad-badge-success' : 'ad-badge-danger'}`}
              >
                {record.isActive ? t('common.active') : t('common.inactive')}
              </span>
            </span>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default RealEstateUsageView;
